import { ReactNode, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChecklistStep } from '../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateMatter } from '@/api/mutations/useCreateMatter';
import { useGetPersonalMatter } from '@/api/queries/useGetPersonalMatter';
import { Matter } from '@/api/types/backend-dto-types';
import FileIcon from '@/assets/guide/homepage-guides/file-icon.svg?react';
import HammerAndWrenchIcon from '@/assets/guide/homepage-guides/hammer-and-wrench-icon.svg?react';
import { useUserContext } from '@/Contexts/User';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { CountBadge } from '@/new/components/count-badge';
import { Icon } from '@/new/components/icon';
import { SubsetCreationMatterSelection } from '@/new/components/onboarding/subset-creation-onboarding';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';

interface ChecklistItem {
  id: ChecklistStep;
  title: string;
  description: string;
  actionText: string;
  action: () => void;
  hidden?: boolean;
  icon: ReactNode;
}

export function OnboardingChecklist({ matters }: { matters?: Matter[] }) {
  const {
    user,
    visibleItems,
    progressPercentage,
    hasExistingMatters,
    isCaseCreationModalOpen,
    setIsCaseCreationModalOpen,
  } = useChecklistState(matters);

  if (!user) {
    return null;
  }

  if (visibleItems.length === 0) {
    return (
      <div className="rounded-md border border-teal-200 bg-teal-50 p-4 text-emerald-900">
        <h2 className="text-lg font-semibold tracking-wide">Onboarding complete!</h2>
        <p className="mt-2 text-sm text-emerald-900/80">
          You've completed our onboarding checklist. Create a new Matter or ask your team members to add you to one.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md">
        {!hasExistingMatters && (
          <>
            <div className="flex flex-col gap-2" data-testid="onboarding-welcome">
              <p className="font-serif text-2xl font-medium text-purple-700">Welcome to Wexler.</p>

              <p className="text-sm text-slate-600">
                Unsure of where to start? Complete our onboarding checklist below.
              </p>
            </div>

            <div className="my-6">
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>{Math.round(progressPercentage)}% complete</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-purple-500 transition-[width] duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </>
        )}

        {visibleItems.length > 0 && (
          <div className="mt-6 text-xs font-semibold text-slate-500">Remaining onboarding items:</div>
        )}

        <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
          {visibleItems.map((item, index) => (
            <ChecklistCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>

      <SubsetCreationMatterSelection
        matters={matters ?? []}
        isOpen={isCaseCreationModalOpen}
        setIsOpen={setIsCaseCreationModalOpen}
      />
    </>
  );
}

export function OnboardingChecklistButton({ matters }: { matters?: Matter[] }) {
  const { user, visibleItems, isCaseCreationModalOpen, setIsCaseCreationModalOpen } = useChecklistState(matters);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!user || visibleItems.length === 0) {
    return null;
  }

  const remainingCount = visibleItems.length;

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button className="relative w-full sm:w-auto">
            <Icon className="lucide--list-check text-purple-600" />
            Onboarding Remaining
            <CountBadge
              count={remainingCount}
              className="-top-2.5 -right-2.25 border-purple-400 bg-purple-50 text-purple-600"
            />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Onboarding checklist</DialogTitle>
            <DialogDescription>Complete these steps to finish getting set up.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            {visibleItems.map((item, index) => (
              <ChecklistCard key={item.id} item={item} index={index} inButton={true} />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <SubsetCreationMatterSelection
        matters={matters ?? []}
        isOpen={isCaseCreationModalOpen}
        setIsOpen={setIsCaseCreationModalOpen}
      />
    </>
  );
}

function useChecklistState(matters?: Matter[]) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const navigate = useNavigate();

  const { data: personalMatter } = useGetPersonalMatter();

  const [isCaseCreationModalOpen, setIsCaseCreationModalOpen] = useState(false);
  const hasExistingMatters = (matters?.length ?? 0) > 0;

  const { mutateAsync: createMatter } = useCreateMatter();

  const handleCreateMatter = async () => {
    await createMatter(undefined, {
      onSuccess: (data) => {
        navigate(`/matters/new/${data.id}`, { state: { fromChecklistMatterCreation: true } });
      },
    });
  };

  const allItems = useMemo<ChecklistItem[]>(() => {
    if (!user) return [];

    return [
      {
        id: ChecklistStep.UploadDocuments,
        title: 'Upload your first documents',
        description: `${capitalizeFirstLetter(t('analyse'))} a document inside your Personal Matter.`,
        actionText: 'Upload documents',
        icon: <FileIcon className="h-4 w-4" />,
        action: () => {
          if (!personalMatter) return;
          navigate(`/matters/${personalMatter.id}/docs/upload`, { state: { fromChecklistDocumentUpload: true } });
          trackCustomEvent(TrackingEventsV2.OnboardingClickUploadDocuments, {
            userId: user.id,
          });
        },
      },
      {
        id: ChecklistStep.CreateMatter,
        title: 'Create your first Matter',
        description: 'Set up a Matter to collaborate with your team.',
        actionText: 'Create a Matter',
        icon: <HammerAndWrenchIcon className="h-4 w-4" />,
        hidden: (matters?.length ?? 0) > 0,
        action: () => {
          trackCustomEvent(TrackingEventsV2.OnboardingClickCreateMatter, {
            userId: user.id,
          });
          handleCreateMatter();
        },
      },
      {
        id: ChecklistStep.CreateCase,
        title: 'Create your first Subset',
        description: 'Create a Subset to organise your documents.',
        actionText: 'Create a Subset',
        icon: <HammerAndWrenchIcon className="h-4 w-4" />,
        action: () => {
          trackCustomEvent(TrackingEventsV2.OnboardingClickCreateSubset, {
            userId: user.id,
          });
          setIsCaseCreationModalOpen(true);
        },
      },
    ];
  }, [matters?.length, navigate, personalMatter, setIsCaseCreationModalOpen, user]);

  const checklistPool = useMemo(() => {
    if (!user) return [];
    if (!user.onboardingChecklistItems) return allItems;
    return allItems.filter((item) => user.onboardingChecklistItems?.includes(item.id));
  }, [allItems, user]);

  const visibleItems = useMemo(() => checklistPool.filter((item) => !item.hidden), [checklistPool]);

  const totalSteps = allItems.length || 1;
  const progressPercentage =
    user?.onboardingChecklistItems === undefined ? 0 : ((totalSteps - visibleItems.length) / totalSteps) * 100;

  return {
    user,
    visibleItems,
    progressPercentage,
    hasExistingMatters,
    isCaseCreationModalOpen,
    setIsCaseCreationModalOpen,
  };
}

const ChecklistCard = ({
  item,
  index,
  inButton = false,
}: {
  item: ChecklistItem;
  index: number;
  inButton?: boolean;
}) => {
  return (
    <div
      className={`flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white/90 p-4 text-sm ${!inButton ? 'flex-col items-start' : ''}`}
    >
      <div className={`flex items-center gap-2`}>
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-50 text-xs font-semibold text-purple-700`}
        >
          {index + 1}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 p-1.5 text-purple-600">
              {item.icon}
            </span>
            {item.title}
          </div>
          <p className="text-xs text-slate-500">{item.description}</p>
        </div>
      </div>
      <div>
        <Button size="sm" variant="outline" className="h-8 self-start px-3 text-xs" onClick={item.action}>
          {item.actionText}
          <Icon className="lucide--arrow-right h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
